/**
 * Song title and artist from the tags inside an audio file (W2-14 onboarding-wizard; DESIGN.md §5.1 step 4 "title /
 * artist pre-filled from tags or file name"). Pure and defensive: malformed tags give no value, never an exception.
 *
 * Read (first found wins, per field):
 *  - ID3v2.2 / 2.3 / 2.4 at the start of MP3, AAC (ADTS) and inside a WAV `id3 ` chunk: TIT2/TT2 and TPE1/TP1, text
 *    encodings ISO-8859-1, UTF-16 with BOM, UTF-16BE and UTF-8; tag-level unsynchronisation and the extended header;
 *  - ID3v1 (the last 128 bytes, "TAG");
 *  - Vorbis comments (TITLE, ARTIST) in FLAC (VORBIS_COMMENT block) and in Ogg Vorbis / Opus (the comment packet,
 *    reassembled from the Ogg pages);
 *  - RIFF INFO (INAM, IART) in WAV;
 *  - MP4 / M4A metadata (moov / udta / meta / ilst, the "nam" and "ART" items with the copyright-sign prefix).
 */

export interface AudioTags {
  readonly title?: string;
  readonly artist?: string;
}

interface MutableTags {
  title?: string;
  artist?: string;
}

function u8(b: Uint8Array, i: number): number {
  return b[i] ?? 0;
}
function u16be(b: Uint8Array, i: number): number {
  return (u8(b, i) << 8) | u8(b, i + 1);
}
function u24be(b: Uint8Array, i: number): number {
  return (u8(b, i) << 16) | (u8(b, i + 1) << 8) | u8(b, i + 2);
}
function u32be(b: Uint8Array, i: number): number {
  return ((u8(b, i) << 24) | (u8(b, i + 1) << 16) | (u8(b, i + 2) << 8) | u8(b, i + 3)) >>> 0;
}
function u32le(b: Uint8Array, i: number): number {
  return (u8(b, i) | (u8(b, i + 1) << 8) | (u8(b, i + 2) << 16) | (u8(b, i + 3) << 24)) >>> 0;
}
function syncsafe(b: Uint8Array, i: number): number {
  return (
    ((u8(b, i) & 0x7f) << 21) | ((u8(b, i + 1) & 0x7f) << 14) | ((u8(b, i + 2) & 0x7f) << 7) | (u8(b, i + 3) & 0x7f)
  );
}
function ascii(b: Uint8Array, i: number, s: string): boolean {
  if (i < 0 || i + s.length > b.length) return false;
  for (let k = 0; k < s.length; k++) if (b[i + k] !== s.charCodeAt(k)) return false;
  return true;
}
function fourcc(b: Uint8Array, i: number): string {
  return String.fromCharCode(u8(b, i), u8(b, i + 1), u8(b, i + 2), u8(b, i + 3));
}

function decode(label: string, bytes: Uint8Array): string {
  try {
    return new TextDecoder(label, { fatal: false }).decode(bytes);
  } catch {
    return '';
  }
}

/** The NUL character (padding and value separator of the tag formats). */
const NUL = String.fromCharCode(0);

/** Trims spaces and NUL padding; empty strings count as missing. */
function clean(s: string): string | undefined {
  let end = s.length;
  while (end > 0 && s.charCodeAt(end - 1) === 0) end--;
  const t = s.slice(0, end).split(NUL).join(' ').trim();
  return t === '' ? undefined : t;
}

function put(out: MutableTags, field: keyof MutableTags, value: string | undefined): void {
  if (value !== undefined && out[field] === undefined) out[field] = value;
}

// ---------------------------------------------------------------------------------------------------------------
// ID3
// ---------------------------------------------------------------------------------------------------------------

/** Text of an ID3v2 text frame body (encoding byte + text; the first of several NUL-separated values). */
function id3Text(body: Uint8Array): string | undefined {
  if (body.length < 1) return undefined;
  const enc = u8(body, 0);
  const data = body.subarray(1);
  let text: string;
  switch (enc) {
    case 0:
      text = decode('latin1', data);
      break;
    case 1:
      // UTF-16 with BOM (each value carries its own BOM); TextDecoder('utf-16le') does not honour a BE BOM.
      text =
        u8(data, 0) === 0xfe && u8(data, 1) === 0xff ? decode('utf-16be', data.subarray(2)) : decode('utf-16le', data);
      text = text.replace(/^\uFEFF/, '');
      break;
    case 2:
      text = decode('utf-16be', data);
      break;
    case 3:
      text = decode('utf-8', data);
      break;
    default:
      return undefined;
  }
  // Several values are NUL separated (ID3v2.4): keep the first.
  const first = text.split(NUL).find((v) => v.trim() !== '') ?? '';
  return clean(first);
}

/** Removes ID3 unsynchronisation (0xFF 0x00 -> 0xFF). */
function unsync(b: Uint8Array): Uint8Array {
  const out = new Uint8Array(b.length);
  let n = 0;
  for (let i = 0; i < b.length; i++) {
    const v = u8(b, i);
    out[n++] = v;
    if (v === 0xff && u8(b, i + 1) === 0x00) i++;
  }
  return out.subarray(0, n);
}

/** Reads an ID3v2 tag that starts at `at`; returns the offset just after it (or `at` when there is none). */
export function readId3v2(b: Uint8Array, at: number, out: MutableTags): number {
  if (!ascii(b, at, 'ID3') || b.length < at + 10) return at;
  const major = u8(b, at + 3);
  const flags = u8(b, at + 5);
  const size = syncsafe(b, at + 6);
  const end = Math.min(b.length, at + 10 + size);
  if (major < 2 || major > 4) return end;
  let tag = b.subarray(at + 10, end);
  if ((flags & 0x80) !== 0 && major < 4) tag = unsync(tag);
  let p = 0;
  if ((flags & 0x40) !== 0 && major >= 3) {
    // Extended header: v2.3 size excludes its own 4 bytes; v2.4 is syncsafe and includes them.
    p = major === 3 ? 4 + u32be(tag, 0) : syncsafe(tag, 0);
  }
  const idLen = major === 2 ? 3 : 4;
  const headLen = major === 2 ? 6 : 10;
  while (p + headLen <= tag.length) {
    if (u8(tag, p) === 0) break;
    const id = String.fromCharCode(...tag.subarray(p, p + idLen));
    const frameSize = major === 2 ? u24be(tag, p + 3) : major === 4 ? syncsafe(tag, p + 4) : u32be(tag, p + 4);
    const frameFlags = major === 2 ? 0 : u16be(tag, p + 8);
    const start = p + headLen;
    const stop = start + frameSize;
    if (frameSize <= 0 || stop > tag.length) break;
    let body = tag.subarray(start, stop);
    if (major === 4) {
      // Per-frame unsynchronisation (0x02) and data length indicator (0x01).
      if ((frameFlags & 0x0001) !== 0) body = body.subarray(4);
      if ((frameFlags & 0x0002) !== 0 || (flags & 0x80) !== 0) body = unsync(body);
    }
    // Compressed or encrypted frames are not read.
    const opaque = major === 3 ? (frameFlags & 0x00c0) !== 0 : major === 4 ? (frameFlags & 0x000c) !== 0 : false;
    if (!opaque) {
      if (id === 'TIT2' || id === 'TT2') put(out, 'title', id3Text(body));
      else if (id === 'TPE1' || id === 'TP1') put(out, 'artist', id3Text(body));
    }
    p = stop;
  }
  return end;
}

function readId3v1(b: Uint8Array, out: MutableTags): void {
  const at = b.length - 128;
  if (at < 0 || !ascii(b, at, 'TAG')) return;
  put(out, 'title', clean(decode('latin1', b.subarray(at + 3, at + 33))));
  put(out, 'artist', clean(decode('latin1', b.subarray(at + 33, at + 63))));
}

// ---------------------------------------------------------------------------------------------------------------
// Vorbis comments (FLAC, Ogg Vorbis, Opus)
// ---------------------------------------------------------------------------------------------------------------

/** Reads a Vorbis comment structure (vendor string, then `KEY=value` entries) starting at `at`. */
export function readVorbisComment(b: Uint8Array, at: number, out: MutableTags): void {
  let p = at;
  const vendorLen = u32le(b, p);
  p += 4 + vendorLen;
  if (p + 4 > b.length) return;
  const count = u32le(b, p);
  p += 4;
  const utf8 = new TextDecoder('utf-8', { fatal: false });
  for (let i = 0; i < count && p + 4 <= b.length; i++) {
    const len = u32le(b, p);
    p += 4;
    if (p + len > b.length) return;
    const entry = utf8.decode(b.subarray(p, p + len));
    p += len;
    const eq = entry.indexOf('=');
    if (eq <= 0) continue;
    const key = entry.slice(0, eq).toUpperCase();
    const value = clean(entry.slice(eq + 1));
    if (key === 'TITLE') put(out, 'title', value);
    else if (key === 'ARTIST') put(out, 'artist', value);
  }
}

function readFlac(b: Uint8Array, out: MutableTags): void {
  let p = 4;
  for (let guard = 0; guard < 128 && p + 4 <= b.length; guard++) {
    const head = u8(b, p);
    const type = head & 0x7f;
    const len = u24be(b, p + 1);
    if (type === 4) readVorbisComment(b, p + 4, out);
    p += 4 + len;
    if ((head & 0x80) !== 0) break;
  }
}

/** The first `max` packets of the first logical Ogg stream (reassembled from page segments). */
function oggPackets(b: Uint8Array, max: number): Uint8Array[] {
  const packets: Uint8Array[] = [];
  let pending: Uint8Array[] = [];
  let serial: number | null = null;
  let p = 0;
  while (p + 27 <= b.length && packets.length < max) {
    if (!ascii(b, p, 'OggS')) break;
    const pageSerial = u32le(b, p + 14);
    const segments = u8(b, p + 26);
    const table = b.subarray(p + 27, p + 27 + segments);
    let q = p + 27 + segments;
    const mine = serial === null || serial === pageSerial;
    if (serial === null) serial = pageSerial;
    for (const lace of table) {
      const piece = b.subarray(q, Math.min(b.length, q + lace));
      q += lace;
      if (!mine) continue;
      pending.push(piece);
      if (lace < 255) {
        const total = pending.reduce((n, x) => n + x.length, 0);
        const packet = new Uint8Array(total);
        let o = 0;
        for (const x of pending) {
          packet.set(x, o);
          o += x.length;
        }
        packets.push(packet);
        pending = [];
        if (packets.length >= max) break;
      }
    }
    p = q;
  }
  return packets;
}

function readOgg(b: Uint8Array, out: MutableTags): void {
  for (const packet of oggPackets(b, 3)) {
    if (u8(packet, 0) === 3 && ascii(packet, 1, 'vorbis')) {
      readVorbisComment(packet, 7, out);
      return;
    }
    if (ascii(packet, 0, 'OpusTags')) {
      readVorbisComment(packet, 8, out);
      return;
    }
  }
}

// ---------------------------------------------------------------------------------------------------------------
// RIFF (WAV)
// ---------------------------------------------------------------------------------------------------------------

function readRiff(b: Uint8Array, out: MutableTags): void {
  let p = 12;
  const end = Math.min(b.length, 8 + u32le(b, 4));
  for (let guard = 0; guard < 4096 && p + 8 <= end; guard++) {
    const id = fourcc(b, p);
    const size = u32le(b, p + 4);
    const body = p + 8;
    if (id === 'LIST' && ascii(b, body, 'INFO')) {
      let q = body + 4;
      const stop = Math.min(end, body + size);
      while (q + 8 <= stop) {
        const sub = fourcc(b, q);
        const len = u32le(b, q + 4);
        const text = b.subarray(q + 8, Math.min(stop, q + 8 + len));
        const value = clean(decode('utf-8', text).includes('\uFFFD') ? decode('latin1', text) : decode('utf-8', text));
        if (sub === 'INAM') put(out, 'title', value);
        else if (sub === 'IART') put(out, 'artist', value);
        q += 8 + len + (len & 1);
      }
    } else if (id === 'id3 ' || id === 'ID3 ') {
      readId3v2(b, body, out);
    }
    p = body + size + (size & 1);
  }
}

// ---------------------------------------------------------------------------------------------------------------
// MP4 / M4A
// ---------------------------------------------------------------------------------------------------------------

const MP4_TITLE = `${String.fromCharCode(0xa9)}nam`;
const MP4_ARTIST = `${String.fromCharCode(0xa9)}ART`;

/** Child boxes of [start, end): [type, bodyStart, end]. */
function boxes(b: Uint8Array, start: number, end: number): Array<readonly [string, number, number]> {
  const list: Array<readonly [string, number, number]> = [];
  let p = start;
  for (let guard = 0; guard < 4096 && p + 8 <= end; guard++) {
    let size = u32be(b, p);
    const type = fourcc(b, p + 4);
    let body = p + 8;
    if (size === 1) {
      size = u32be(b, p + 8) * 0x100000000 + u32be(b, p + 12);
      body = p + 16;
    } else if (size === 0) {
      size = end - p;
    }
    if (size < 8 || p + size > end) break;
    list.push([type, body, p + size]);
    p += size;
  }
  return list;
}

function child(b: Uint8Array, parent: readonly [string, number, number], type: string, skip = 0) {
  return boxes(b, parent[1] + skip, parent[2]).find(([t]) => t === type);
}

function readMp4(b: Uint8Array, out: MutableTags): void {
  const moov = boxes(b, 0, b.length).find(([t]) => t === 'moov');
  if (moov === undefined) return;
  const udta = child(b, moov, 'udta');
  const meta = udta === undefined ? undefined : child(b, udta, 'meta');
  // `meta` is a full box (4 bytes of version and flags before its children); QuickTime files omit them.
  const ilst = meta === undefined ? undefined : (child(b, meta, 'ilst', 4) ?? child(b, meta, 'ilst'));
  if (ilst === undefined) return;
  for (const item of boxes(b, ilst[1], ilst[2])) {
    const field = item[0] === MP4_TITLE ? 'title' : item[0] === MP4_ARTIST ? 'artist' : null;
    if (field === null) continue;
    const data = child(b, item, 'data');
    // data box: 4 bytes type indicator (1 = UTF-8) and 4 bytes locale before the value.
    if (data !== undefined && u32be(b, data[1]) === 1) {
      put(out, field, clean(decode('utf-8', b.subarray(data[1] + 8, data[2]))));
    }
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------------------------------------------

/** Title and artist found in the file's tags (fields left out when not present). */
export function readAudioTags(bytes: Uint8Array): AudioTags {
  const out: MutableTags = {};
  try {
    if (ascii(bytes, 0, 'fLaC')) readFlac(bytes, out);
    else if (ascii(bytes, 0, 'OggS')) readOgg(bytes, out);
    else if (ascii(bytes, 0, 'RIFF') && ascii(bytes, 8, 'WAVE')) readRiff(bytes, out);
    else if (ascii(bytes, 4, 'ftyp')) readMp4(bytes, out);
    else {
      const after = readId3v2(bytes, 0, out);
      // FLAC files sometimes carry an ID3v2 tag in front of "fLaC".
      if (after > 0 && ascii(bytes, after, 'fLaC')) readFlac(bytes.subarray(after), out);
      else readId3v1(bytes, out);
    }
  } catch {
    // Truncated or odd tags: whatever was read so far.
  }
  return out;
}
